import { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";

import apiClient from "../../api-client";
import { getPlanLimitError } from "../../utils/planLimits";
import { useAuth } from "../contexts/AuthContext";
import { getData } from "../hooks/getData";
import { UnifiedModal } from "../UnifiedModal";
import { Label } from "../Label";
import { CustomSelector } from "../CustomSelector";
import { CustomTextField } from "../CustomTextField";
import { Button } from "../Button";
import { InfoTooltip } from "../InfoTooltip";

const STATUS_ACTIVE = "active";
const STATUS_PAUSED = "paused";

const statusOptions = [
  { value: STATUS_ACTIVE, label: "Activado" },
  { value: STATUS_PAUSED, label: "No activado" },
];

export const RecurringProjectConfigModal = ({
  open,
  onClose,
  clientId,
  clientName,
  refreshKey = 0,
  appRefreshKey = 0,
  onSaved,
  onError,
  tutorialData = null,
  dialogProps = null,
}) => {
  const { interceptorReady } = useAuth();
  const tutorialMode = Boolean(tutorialData);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [nextRunDate, setNextRunDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [localError, setLocalError] = useState("");

  const subscriptionEndpoint = useMemo(() => {
    if (!clientId) return null;
    return `clients/${clientId}/recurring-subscription/`;
  }, [clientId]);

  const {
    data: subscription,
    error: subscriptionError,
    isLoading: isLoadingSubscription,
    isFetching: isFetchingSubscription,
  } = getData(
    subscriptionEndpoint,
    Boolean(open && interceptorReady && subscriptionEndpoint && !tutorialMode),
    [subscriptionEndpoint, refreshKey, appRefreshKey]
  );

  const resolvedSubscription = tutorialMode ? tutorialData : subscription;
  const isLoading = tutorialMode
    ? false
    : isLoadingSubscription || isFetchingSubscription;

  const clientOptions = useMemo(() => {
    if (!clientId) return [];
    return [
      {
        value: clientId,
        label: clientName || `Cliente ${clientId}`,
      },
    ];
  }, [clientId, clientName]);

  useEffect(() => {
    if (!open) return;

    setSelectedStatus(resolvedSubscription?.status || "");
    setNextRunDate(resolvedSubscription?.next_run_date || "");
    setLocalError("");
    setErrorMessage("");
  }, [open, resolvedSubscription?.status, resolvedSubscription?.next_run_date]);

  useEffect(() => {
    if (!open) return;
    if (tutorialMode) return;
    if (!subscriptionError) return;

    const responseData = subscriptionError?.response?.data;
    if (typeof responseData === "string") {
      setErrorMessage(responseData);
      return;
    }
    if (responseData?.detail) {
      setErrorMessage(responseData.detail);
      return;
    }
    setErrorMessage("No se pudo cargar la configuración de recurrencia.");
  }, [open, subscriptionError, tutorialMode]);

  const hasChanges = Boolean(
    !tutorialMode &&
    subscription &&
    ((selectedStatus && selectedStatus !== subscription.status) ||
      (nextRunDate && nextRunDate !== subscription.next_run_date))
  );

  const handleSave = async () => {
    if (!selectedStatus || !nextRunDate) {
      setLocalError("Debes completar estado y próxima fecha.");
      return;
    }

    if (!subscriptionEndpoint) {
      setLocalError("No se pudo identificar el cliente.");
      return;
    }

    setLocalError("");
    setErrorMessage("");
    setIsSaving(true);
    try {
      const response = await apiClient.patch(subscriptionEndpoint, {
        status: selectedStatus,
        next_run_date: nextRunDate,
      });
      onSaved?.(response.data);
      onClose?.();
    } catch (error) {
      // Lo muestra PlanFeatureRequiredDialog
      if (getPlanLimitError(error)) return;
      const responseData = error?.response?.data;
      if (typeof responseData === "string") {
        setErrorMessage(responseData);
      } else if (responseData?.detail) {
        setErrorMessage(responseData.detail);
      } else {
        setErrorMessage(
          "No se pudo actualizar la configuración de recurrencia."
        );
      }
      onError?.(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title="Configurar recurrencia de proyectos"
      maxWidth="sm"
      {...dialogProps}
    >
      <Stack spacing={2.5} data-tutorial={tutorialMode ? "tutorial-recurring-config-modal" : undefined}>
        <Box>
          <Label>Cliente</Label>
          <Box sx={{ mt: 1 }}>
            <CustomSelector
              value={clientId || ""}
              options={clientOptions}
              onChange={() => { }}
              placeholder="Cliente"
              disabled
            />
          </Box>
        </Box>

        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Cargando configuración de recurrencia...
          </Typography>
        ) : (
          <>
            <Box>
              <InfoTooltip
                title="Al activar la recurrencia, se generará un proyecto cada mes. Puedes consultar la próxima fecha del proyecto recurrente en “Próxima fecha”."
                zIndex={100001}
              >
                <Label>Estado actual</Label>
              </InfoTooltip>
              <Box sx={{ mt: 1 }}>
                <CustomSelector
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  options={statusOptions}
                  placeholder="Selecciona estado"
                  disabled={
                    isSaving ||
                    !resolvedSubscription ||
                    tutorialMode
                  }
                />
              </Box>
            </Box>

            <Box>
              <Label>Próxima fecha</Label>
              <Box sx={{ mt: 1 }}>
                <CustomTextField
                  type="date"
                  value={nextRunDate}
                  onChange={(event) => setNextRunDate(event.target.value)}
                  disabled={
                    isSaving ||
                    !resolvedSubscription ||
                    tutorialMode
                  }
                />
              </Box>
            </Box>
          </>
        )}

        {errorMessage || localError ? (
          <Typography variant="body2" sx={{ color: "error.main" }}>
            {localError || errorMessage}
          </Typography>
        ) : null}

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          {tutorialMode ? (
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={isLoading || isSaving || !hasChanges}
              >
                Guardar cambios
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </UnifiedModal>
  );
};

export default RecurringProjectConfigModal;
